import { Button, Grid } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddRounded as AddRoundedIcon } from '@mui/icons-material'
import AccountHeader from '../AccountHeader'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { FetchBrandingOptions } from '../../UserData/_actions/BrandingOptionActions'
import { type BrandingOptionStore } from '../../UserData/_stores/BrandingOptionStore'
import BrandingOption from './BrandingOption'
import BrandingOptionSkeleton from './BrandingOptionSkeleton'
import AddBrandingOptionDialog from './AddBrandingOptionDialog'
import AccountEmptyCard from '../AccountEmptyCard'

const LOADING_ITEM_COUNT = 6

export default function BrandingOptionList() {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')

  const brandingOptions = useAppSelector(state => state.get('userData').get('brandingOptions'))
  const loading = useAppSelector(state => state.get('UIData').get('loaders').get('fetch_branding_options'))

  const [addDialogOpened, setAddDialogOpened] = useState(false)

  const loadingGrid = useMemo(() => {
    const listSkeletons = []

    for (let i = 0; i < LOADING_ITEM_COUNT; i++) {
      listSkeletons.push(<BrandingOptionSkeleton key={i} />)
    }

    return listSkeletons
  }, [])

  useEffect(() => {
    dispatch(FetchBrandingOptions())
  }, [])

  const onAddOpen = useCallback(() => {
    setAddDialogOpened(true)
  }, [])

  const onAddClose = useCallback(() => {
    setAddDialogOpened(false)
  }, [])

  const renderBrandingOption = useCallback((brandingOption: BrandingOptionStore) => {
    return <BrandingOption
      key={brandingOption.get('id')}
      brandingOption={brandingOption}
    />
  }, [])

  return <>
    <AccountHeader
      title={t('Branding options')}
      actions={brandingOptions.count() > 0 ? <Button
        variant="contained"
        color="accent"
        startIcon={<AddRoundedIcon />}
        onClick={onAddOpen}
      >{t('Create Branding option')}</Button> : null}
    />

    <Grid container spacing={2}>
      {loading
        ? loadingGrid
        : brandingOptions.count() > 0
          ? brandingOptions.valueSeq().map(renderBrandingOption)
          : <Grid item xs={12} md={12} lg={6} xl={4}>
            <AccountEmptyCard
              title={t('No branding options yet')}
              description={t('Create labels, stickers, or inserts to start branding your orders.')}
              actionLabel={t('Create Branding option')}
              onAction={onAddOpen}
            />
          </Grid>}
    </Grid>

    <AddBrandingOptionDialog
      opened={addDialogOpened}
      onClose={onAddClose}
    />
  </>
}
